import { revealPassword } from '../utils/passwordReveal';
import { initTurnstile, withTurnstile } from '../utils/turnstile';
import { registerSubmitGate } from '../utils/submitGate';
import { AuthenticationType } from '../utils/authenticationType';

/**
 * Handles UI and form submission events for login and registration forms (main and checkout versions).
 *
 * Handles form-specific behaviour such as password reveal toggles,
 * submit/loading states, keyboard interactions, DOB field-level and overall validation,
 * and accessible tab-style switching between login and register panels.
 *
 * Safely no-ops when expected DOM elements are not present.
 *
 * @returns {void}
 */
export function loginOrRegisterListeners(): void {
	const registrationForm = document.getElementById('registrationForm') as HTMLFormElement | null;
	const loginForm = document.getElementById('loginForm') as HTMLFormElement | null;
	const loginPage = document.body.classList.contains('login');

	// The checkout step has its own listeners for these forms: they post over AJAX and re-render
	// the step in place, so it is the checkout module that submits them. Submitting them from here
	// as well navigates the whole page, which is a full reload the customer should never see.
	const checkoutFlow = document.body.classList.contains('checkout');
	const passwordReset = document.getElementById('password-reset') as HTMLElement | null;

	/**
	 * Handles registration UI logic.
	 */
	if (registrationForm) {
		const registerSubmitBtn = document.getElementById(
			'auth__submit-register',
		) as HTMLButtonElement | null;
		if (!registerSubmitBtn) return;

		const over18Checkbox = document.getElementById('over18') as HTMLInputElement | null;
		if (over18Checkbox) {
			const gate = registerSubmitGate(registerSubmitBtn, () => over18Checkbox.checked);
			over18Checkbox.addEventListener('change', () => gate.evaluate());
		}

		// Populate necessary form hidden fields (standard register view)
		const registerInput = document.querySelector(
			'input[name="newCustomerUsername"]',
		) as HTMLInputElement | null;
		const emailInput = document.getElementById('emailHandle') as HTMLInputElement | null;

		if (emailInput && registerInput) {
			registerSubmitBtn.addEventListener('click', () => {
				emailInput.value = registerInput.value;
			});
		}

		if (!checkoutFlow) {
			registrationForm.addEventListener(
				'submit',
				withTurnstile(registrationForm, 'register', () => registrationForm.submit(), true),
			);
		}

		// Virgin Red inputs handling (checkout registration)
		const vrCheckbox = registrationForm.querySelector(
			'input[name="virginRedCheckbox"]',
		) as HTMLInputElement | null;
		if (vrCheckbox) {
			const vrTextInputWrapper = registrationForm.querySelector(
				'.virgin-members__input-wrapper',
			) as HTMLElement | null;
			const vrTextInput = registrationForm.querySelector(
				'input[name="virginRedNumber"]',
			) as HTMLInputElement | null;
			if (!vrTextInputWrapper) return;
			vrCheckbox.addEventListener('change', function () {
				vrTextInputWrapper.classList.toggle('hidden');
				vrTextInputWrapper.addEventListener(
					'transitionend',
					() => {
						if (vrTextInput) {
							vrTextInput.setAttribute('tabindex', '0');
							vrTextInput.focus();
						}
					},
					{ once: true },
				);
			});

			if (vrCheckbox.checked) {
				vrTextInputWrapper.classList.toggle('hidden');
			}
		}
	}

	/**
	 * Handles login keyboard behaviour and password reveal toggling.
	 */
	if (loginForm) {
		// Allow Enter in password field to submit the form
		loginForm.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key !== 'Enter') return;
			const target = e.target as HTMLElement;
			if (target instanceof HTMLInputElement && target.type === 'password') {
				e.preventDefault();
				loginForm.requestSubmit();
			}
		});

		if (!checkoutFlow) {
			loginForm.addEventListener(
				'submit',
				withTurnstile(loginForm, 'login', () => loginForm.submit(), true),
			);
		}
	}

	/**
	 * Shared behaviour for both forms (layout and focus management).
	 */

	// *** Narrow to non-null locals for TS ***
	const login = loginForm as HTMLFormElement;
	const register = registrationForm as HTMLFormElement;

	// Tabs: buttons with role="tab" controlling form-as-tabpanel
	const tabLogin = document.getElementById('tab-login') as HTMLButtonElement | null;
	const tabRegister = document.getElementById('tab-register') as HTMLButtonElement | null;
	const registerLink = document.getElementById('register-link') as HTMLAnchorElement | null;
	const loginLink = document.getElementById('login-link') as HTMLAnchorElement | null;

	/**
	 * Toggles focusability for all controls inside a form.
	 *
	 * @param {HTMLFormElement} form - The form whose descendants will be toggled.
	 * @param {boolean} disable - When true, controls are removed from tab order and hidden from AT.
	 * @returns {void}
	 */
	function setDisabled(form: HTMLFormElement, disable: boolean): void {
		form.querySelectorAll<HTMLElement>('input, button, select, textarea, a').forEach((el) => {
			if (disable) {
				el.setAttribute('tabindex', '-1');
				el.setAttribute('aria-hidden', 'true');
			} else {
				el.removeAttribute('tabindex');
				el.removeAttribute('aria-hidden');
			}
		});
	}

	/**
	 * Activates either the login or register panel and updates ARIA state.
	 *
	 * @param {AuthenticationType} which - The panel to activate.
	 * @returns {void}
	 */
	function activate(which: AuthenticationType): void {
		const isLogin = which === 'login';

		// aria-selected
		if (tabLogin) tabLogin.setAttribute('aria-selected', isLogin ? 'true' : 'false');
		if (tabRegister) tabRegister.setAttribute('aria-selected', isLogin ? 'false' : 'true');

		// swap image displayed based on tab clicked (/login page only)
		if (loginPage) {
			const authImage = document.querySelector('.auth-image') as HTMLElement;
			const authImageLogin = document.querySelector('.auth-image__login') as HTMLElement;
			const authImageRegister = document.querySelector(
				'.auth-image__register',
			) as HTMLElement;
			if (!authImage && !authImageLogin && !authImageRegister) return;

			if (isLogin) {
				authImageLogin.classList.remove('hidden');
				authImageRegister.classList.add('hidden');
			} else {
				authImageRegister.classList.remove('hidden');
				authImageLogin.classList.add('hidden');
			}
		}

		// show/hide panels
		login.toggleAttribute('hidden', !isLogin);
		register.toggleAttribute('hidden', isLogin);

		// focusability
		setDisabled(login, !isLogin);
		setDisabled(register, isLogin);

		// move focus into active panel’s first control
		const firstControl = (isLogin ? login : register).querySelector<HTMLElement>(
			'input, select, textarea, button',
		);

		if (firstControl) firstControl.focus({ preventScroll: false });
	}

	// click handlers (Enter/Space already work on <button>)
	if (tabLogin) tabLogin.addEventListener('click', () => activate('login'));
	if (tabRegister) tabRegister.addEventListener('click', () => activate('register'));
	if (registerLink)
		registerLink.addEventListener('click', (event: Event) => {
			event.preventDefault();
			tabRegister?.focus();
			tabRegister?.click();
		});
	if (loginLink)
		loginLink.addEventListener('click', (event: Event) => {
			event.preventDefault();
			tabLogin?.focus();
			tabLogin?.click();
		});

	const authSection = document.querySelector('.auth') as HTMLElement | null;

	activate(authSection?.dataset.customerAction === 'login' ? 'login' : 'register');

	// Enable password reveal toggle
	const passwordReveal = document.querySelectorAll<HTMLElement>('.password__reveal');
	if (passwordReveal) {
		passwordReveal.forEach((el) => {
			el.addEventListener('click', () => revealPassword(el));
			el.addEventListener('keydown', (e: KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					revealPassword(el);
				}
			});
		});
	}

	/**
	 * Various button functions for Password Reset views
	 */
	if (passwordReset) {
		// Add loading spinner states to all buttons when clicked
		const passwordResetBtns = passwordReset?.querySelectorAll<HTMLElement>(
			'button, .password-reset__btn',
		);
		if (passwordResetBtns) {
			passwordResetBtns.forEach((el) => {
				el.addEventListener('click', () => {
					el.classList.add('btn-loading');
				});
			});
		}
	}

	if (!checkoutFlow) {
		initTurnstile();
	}
}

/**
 * Show a field error message and mark invalid.
 * @param {HTMLInputElement} input - Target input element.
 * @param {HTMLElement|null} errorEl - Field error element.
 * @param {string} message - Error text.
 * @returns {void}
 */
function setFieldError(
	input: HTMLInputElement,
	errorEl: HTMLElement | null,
	message: string,
): void {
	if (errorEl) {
		errorEl.textContent = message;
		errorEl.hidden = false;
	}
	input.setAttribute('aria-invalid', 'true');
}

/**
 * Clear a field error and invalid state.
 * @param {HTMLInputElement} input - Target input element.
 * @param {HTMLElement|null} errorEl - Field error element.
 * @returns {void}
 */
function clearFieldError(input: HTMLInputElement, errorEl: HTMLElement | null): void {
	if (errorEl) errorEl.hidden = true;
	input.removeAttribute('aria-invalid');
}

/**
 * Restrict to digits, enforce range on blur, pad only when valid,
 * and show a generic field-level message when invalid.
 * @param {HTMLInputElement} input - Input to validate.
 * @param {number} minValue - Inclusive min.
 * @param {number} maxValue - Inclusive max.
 * @param {number} padLength - Digits to pad valid values to.
 * @param {HTMLElement|null} errorEl - Field error element.
 * @param {string} invalidMsg - Generic message, e.g. "Please enter a valid day."
 * @returns {void}
 */
export function restrictToRange(
	input: HTMLInputElement,
	minValue: number,
	maxValue: number,
	padLength: number,
	errorEl: HTMLElement | null,
	invalidMsg: string,
): void {
	input.addEventListener('input', () => {
		let raw = input.value.replace(/\D/g, '');
		if (raw.length > padLength) raw = raw.slice(0, padLength);
		input.value = raw;
		clearFieldError(input, errorEl);
	});

	input.addEventListener('blur', () => {
		if (input.value.length === 0) {
			clearFieldError(input, errorEl);
			return;
		}
		const numeric = parseInt(input.value, 10);
		if (isNaN(numeric) || numeric < minValue || numeric > maxValue) {
			setFieldError(input, errorEl, invalidMsg);
			return;
		}
		clearFieldError(input, errorEl);
		input.value = numeric.toString().padStart(padLength, '0');
	});
}

/**
 * True if input value is within [min,max].
 * @param {HTMLInputElement} input - Input field.
 * @param {number} min - Min value.
 * @param {number} max - Max value.
 * @returns {boolean}
 */
export function isValidInput(input: HTMLInputElement, min: number, max: number): boolean {
	const num = parseInt(input.value, 10);
	return !isNaN(num) && num >= min && num <= max;
}

/**
 * Validate combined DOB as a real calendar date and surface an overall message.
 * @param {HTMLInputElement} day - Day input.
 * @param {HTMLInputElement} month - Month input.
 * @param {HTMLInputElement} year - Year input.
 * @param {HTMLElement|null} overallErrorEl - Overall DOB error element.
 * @returns {void}
 */
export function validateFullDob(
	day: HTMLInputElement,
	month: HTMLInputElement,
	year: HTMLInputElement,
	overallErrorEl: HTMLElement | null,
): void {
	if (!day.value || !month.value || !year.value) {
		if (overallErrorEl) overallErrorEl.hidden = true;
		return;
	}

	const d = parseInt(day.value, 10);
	const m = parseInt(month.value, 10);
	const y = parseInt(year.value, 10);

	// Construct and compare to detect impossible dates (e.g., 31/02/2024)
	const dt = new Date(y, m - 1, d);
	const isReal = dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;

	if (!overallErrorEl) return;
	if (!isReal) {
		overallErrorEl.textContent = 'Please enter a valid date.';
		overallErrorEl.hidden = false;
		return;
	} else {
		overallErrorEl.hidden = true;
	}
}
